import { AlertCircle, AlertTriangle, ArrowLeft, Info, Undo2, Wallet } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { z } from "zod";

import {
  ConsequenceList,
  ConsequenceRow,
} from "@/modules/dashboard/payout-batches/components/execute/ConsequenceList";
import { useArmDelay } from "@/modules/dashboard/payout-batches/hooks/useArmDelay";
import { WalletTopUpSuccess } from "@/modules/dashboard/wallet-top-ups/components/WalletTopUpSuccess";
import type { WalletTopUpCreated } from "@/modules/dashboard/wallet-top-ups/types/walletTopUps.types";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaIsotype } from "@/shared/components/data-display/ColocaIsotype";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaAmountInput } from "@/shared/components/forms/ColocaAmountInput";
import { ColocaCheckbox } from "@/shared/components/forms/ColocaCheckbox";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaTextareaField } from "@/shared/components/forms/ColocaTextareaField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaMoneyTransit } from "@/shared/components/motion/ColocaMoneyTransit";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import { TwoFactorActionModals } from "@/shared/components/overlays/TwoFactorActionModals";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { readApiCode } from "@/shared/utils/apiError";
import { formatCurrencyAmount, minorUnitsFor } from "@/shared/utils/currencyMinorUnits";
import { trimTrailingZeros } from "@/shared/utils/decimal";

import { INCIDENT_REFUND_NOTE_MAX } from "../constants/paymentIncidents.constants";
import { useIncidentRefund } from "../hooks/useIncidentRefund";
import type { PaymentIncidentDetail } from "../types/paymentIncidents.types";
import { refundAmountProblem } from "../utils/paymentIncidents";

const FORM_ID = "incident-client-refund";

type Step = "form" | "review" | "done";

export interface IncidentRefundDialogProps {
  open: boolean;
  incident: PaymentIncidentDetail;
  provider: string;
  onClose: () => void;
  /** The incident as the refund left it, to show at once. */
  onRefunded: (incident: PaymentIncidentDetail) => void;
  /** After a failure: read the incident again, its refund may have gone through. */
  onStale: () => void;
}

/** Refund the client while the provider resolves the claim. Coloca's money until the provider returns it. */
export function IncidentRefundDialog(props: IncidentRefundDialogProps) {
  if (!props.open) return null;
  return <IncidentRefundDialogContent {...props} />;
}

function IncidentRefundDialogContent({
  incident,
  provider,
  onClose,
  onRefunded,
  onStale,
}: IncidentRefundDialogProps) {
  const { t, activeLanguage } = useTranslations("paymentIncidents");
  const refund = incident.refund;
  const currency = refund.currency;
  const digits = minorUnitsFor(currency);
  const [step, setStep] = useState<Step>("form");
  const [values, setValues] = useState<{ amount: string; note: string } | null>(null);
  const [reviewed, setReviewed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<WalletTopUpCreated | null>(null);
  const { armed } = useArmDelay(step === "review");
  const { submit, renewKey, twoFactor } = useIncidentRefund(incident.id);
  const lastSent = useRef<string | null>(null);

  const schema = useMemo(
    () =>
      z
        .object({ amount: z.string(), note: z.string().max(INCIDENT_REFUND_NOTE_MAX) })
        .superRefine((form, ctx) => {
          const problem = refundAmountProblem(form.amount, refund.max_amount, currency);
          if (!problem) return;
          ctx.addIssue({
            code: "custom",
            path: ["amount"],
            message: t(`refund_validation_${problem}`, {
              max: formatCurrencyAmount(refund.max_amount, currency, activeLanguage),
              currency,
              digits: String(digits),
            }),
          });
        }),
    [activeLanguage, currency, digits, refund.max_amount, t],
  );
  const form = useZodForm(schema, {
    amount: trimTrailingZeros(refund.max_amount),
    note: "",
  });
  const operation = incident.operation;
  const organizationName = incident.organization_name ?? t("organization_unknown");
  // The money travels only once the code left. Asking for an email code also runs, with the challenge still
  // open for the code to be typed — nothing has been sent then.
  const processing = twoFactor.isRunning && !twoFactor.challenge.open;

  const confirm = async () => {
    if (!values) return;
    const body = JSON.stringify(values);
    if (lastSent.current !== null && lastSent.current !== body) renewKey();
    lastSent.current = body;
    setError(null);
    try {
      const result = await submit(values.amount, values.note || undefined);
      if (!result) return;
      setCreated(result.top_up);
      onRefunded(result.incident);
      setStep("done");
    } catch (failure: unknown) {
      setError(
        readApiCode(failure) === "incident_already_refunded"
          ? t("refund_error_already")
          : HttpApiService.getErrorMessage(failure),
      );
      onStale();
    }
  };

  return (
    <>
      <ColocaDialog
        open
        onOpenChange={(next) => {
          if (!next && !processing) onClose();
        }}
        size="lg"
        title={t(
          step === "done" ? "refund_title_done" : step === "review" ? "refund_title_review" : "refund_title",
        )}
        description={
          step === "done"
            ? t("refund_description_done")
            : t("refund_description", { provider, organization: organizationName })
        }
        showCloseButton={!processing}
        footer={
          processing ? null : step === "form" ? (
            <>
              <ColocaButton variant="ghost" onClick={onClose}>
                {t("action_cancel")}
              </ColocaButton>
              <ColocaButton type="submit" form={FORM_ID} variant="primary">
                {t("refund_review")}
              </ColocaButton>
            </>
          ) : step === "review" ? (
            <>
              <ColocaButton variant="ghost" icon={ArrowLeft} onClick={() => setStep("form")}>
                {t("action_back")}
              </ColocaButton>
              <ColocaButton
                variant="primary"
                icon={Undo2}
                disabled={!reviewed || !armed}
                onClick={() => void confirm()}
              >
                {t("refund_confirm")}
              </ColocaButton>
            </>
          ) : (
            <ColocaButton variant="primary" onClick={onClose}>
              {t("action_close")}
            </ColocaButton>
          )
        }
      >
        {step === "form" && (
          <ColocaForm
            id={FORM_ID}
            form={form}
            onSubmit={(next) => {
              setValues({ amount: next.amount.trim(), note: next.note.trim() });
              setReviewed(false);
              setError(null);
              setStep("review");
            }}
            className="space-y-5"
          >
            <ColocaAmountInput
              name="amount"
              label={t("refund_field_amount")}
              description={t("refund_field_amount_hint", {
                max: formatCurrencyAmount(refund.max_amount, currency, activeLanguage),
                currency,
              })}
              suffix={currency}
              maxFractionDigits={digits}
            />
            <ColocaTextareaField
              name="note"
              label={t("refund_field_note")}
              description={t("refund_field_note_hint")}
              rows={3}
              maxLength={INCIDENT_REFUND_NOTE_MAX}
              count
            />
          </ColocaForm>
        )}

        {step === "review" && values && !processing && (
          <div className="space-y-5">
            <ConsequenceList>
              <ConsequenceRow label={t("refund_review_organization")} value={organizationName} />
              <ConsequenceRow
                label={t("refund_review_wallet")}
                value={t("refund_review_wallet_value", { currency })}
              />
              {operation && (
                <ConsequenceRow
                  label={t("refund_review_charged")}
                  value={
                    <ColocaAmountDisplay
                      value={operation.total_debited}
                      currency={currency}
                      fractionDigits={digits}
                      locale={activeLanguage}
                      size="sm"
                    />
                  }
                />
              )}
              <ConsequenceRow
                label={t("refund_review_amount")}
                emphasis
                value={
                  <span className="text-primary">
                    <ColocaAmountDisplay
                      value={values.amount}
                      currency={currency}
                      fractionDigits={digits}
                      locale={activeLanguage}
                      size="lg"
                      signed
                    />
                  </span>
                }
              />
            </ConsequenceList>
            {values.note && (
              <div className="text-sm">
                <p className="text-xs text-muted-foreground">{t("refund_field_note")}</p>
                <p className="mt-0.5 break-words text-foreground">{values.note}</p>
              </div>
            )}
            <ColocaAlert variant="secondary" icon={Info} text={t("refund_review_client_sees")} />
            <ColocaAlert
              variant="warning"
              icon={AlertTriangle}
              text={t("refund_review_coloca_money", { provider })}
            />
            {error && <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />}
            <ColocaCheckbox
              checked={reviewed}
              onCheckedChange={setReviewed}
              label={t("refund_review_checkbox")}
            />
          </div>
        )}

        {processing && values && (
          <div role="status" className="flex flex-col items-center gap-6 py-6 text-center">
            <ColocaMoneyTransit
              className="max-w-md"
              label={t("refund_transit_aria", {
                amount: formatCurrencyAmount(values.amount, currency, activeLanguage),
                currency,
                name: organizationName,
              })}
              from={{
                icon: <ColocaIsotype className="size-6" />,
                label: t("refund_transit_from"),
                tone: "brand",
              }}
              to={{
                icon: <Wallet className="size-5" />,
                label: organizationName,
                caption: t("refund_review_wallet_value", { currency }),
              }}
              legs={[{ rail: { kind: "fiat" }, asset: { kind: "fiat", currency } }]}
            />
            <div className="text-primary">
              <ColocaAmountDisplay
                value={values.amount}
                currency={currency}
                fractionDigits={digits}
                size="lg"
                signed
              />
            </div>
          </div>
        )}

        {step === "done" && created && <WalletTopUpSuccess topUp={created} />}
      </ColocaDialog>
      <TwoFactorActionModals
        controller={twoFactor}
        requiredDescription={t("refund_two_factor_required")}
        challengeDescription={t("refund_two_factor_description")}
      />
    </>
  );
}
